import type { ReactNode } from "react";
import type {
  AttributeValueNodeData,
  BoardMaterialPosition,
  BoardMaterialScope,
  CompareNodeData,
  FloatOperationNodeData,
  IntegerOperationNodeData,
  PropNodeType,
  PrototypeLibraryNodeData,
  RoundNumberNodeData,
} from "../../shared/types/PropGraph";
import type { PropGraphNodeParameterDoc, PropGraphNodeUsageDoc } from "./types";

type OptionValue = string | number;

type DescribedOption<T extends OptionValue = string> = {
  value: T;
  label: string;
  description?: string;
};

export const propGraphActionNodeTypes: PropNodeType[] = [
  "actionGrow",
  "actionExtraYield",
  "actionPermanentBoost",
  "actionPurify",
  "actionProtect",
  "actionAccelerate",
  "actionTransform",
  "actionSpawn",
  "actionEliminate",
  "actionCollect",
  "actionHarvest",
  "actionRarityWeight",
  "actionExtendCommission",
  "actionSwapPosition",
  "actionTriggerVisual",
];

export const propGraphNodeDocumentLabels: Record<PropNodeType, string> = {
  eventTurnStart: "回合开始",
  eventAcquire: "持有触发",
  eventUse: "主动使用",
  eventEliminated: "消除触发",
  targetSelf: "自身",
  targetBoard: "盘面原料",
  prototypeLibrary: "原型库",
  prototypeSelect: "原型",
  targetFilter: "条件筛选",
  filterRarity: "品质筛选",
  filterTag: "标签筛选",
  filterPrototype: "原型筛选",
  filterRandom: "随机筛选",
  actionGrow: "成长",
  actionExtraYield: "额外产出",
  actionPurify: "净化",
  actionProtect: "保护",
  actionAccelerate: "加速",
  actionTransform: "转化",
  actionSpawn: "生成",
  actionEliminate: "消除",
  actionCollect: "收集",
  actionHarvest: "收获",
  actionRarityWeight: "品质权重",
  actionExtendCommission: "延长调和",
  actionSwapPosition: "调换位置",
  actionPermanentBoost: "永久加成",
  actionTriggerVisual: "触发表现",
  intConstant: "整数常量",
  intAttribute: "整数属性",
  intQuantity: "数量",
  intKindCount: "种类",
  intRandom: "整数随机",
  intOperation: "整数运算",
  floatConstant: "浮点常量",
  floatAttribute: "浮点属性",
  floatRandom: "浮点随机",
  floatOperation: "浮点运算",
  roundNumber: "取整",
  rarityConstant: "品质常量",
  tagConstant: "标签常量",
  logicCompareInt: "整数比较",
  logicCompareFloat: "浮点比较",
  logicContains: "包含",
  logicProbability: "概率",
};

export const propGraphNodeDocumentSummaries: Record<PropNodeType, string> = {
  eventTurnStart: "在回合开始时启动流程，是原料和配方最常用的周期触发入口。",
  eventAcquire: "道具处于持有状态时持续启动流程；道具从材料库或配方列表移除后，对应效果应停止。",
  eventUse: "玩家主动使用辅料时启动流程，适合一次性效果、延迟收获或持续加成类辅料。",
  eventEliminated: "监听指定目标被消除的时机，并在目标被消除后启动流程。",
  targetSelf: "输出当前道具实例，用于描述作用于自身的效果。",
  targetBoard: "从盘面中按全部、相对位置或绝对位置取原料目标数组。",
  prototypeLibrary: "输出指定类型的道具原型集合，常用于随机生成或随机转化。",
  prototypeSelect: "输出一个固定道具原型，常用于生成、转化或原型筛选。",
  targetFilter: "用布尔条件过滤目标数组，条件不成立时不输出目标。",
  filterRarity: "按一个或多个品质过滤目标数组。",
  filterTag: "按一个或多个标签过滤目标数组。",
  filterPrototype: "按一个或多个道具原型过滤目标数组；比较的是实例的原型 ID。",
  filterRandom: "从输入目标数组中随机保留指定数量。",
  actionGrow: "永久改变目标的魔力产出，适合成长、衰减和长期培养效果。",
  actionExtraYield: "在本次反应中立即增加或减少魔力，也可按倍率放大目标产出。",
  actionPurify: "移除目标身上的负面效果，负面效果由动作节点的负面参数标记。",
  actionProtect: "保护目标不被消除，适合核心原料、蘑菇续航和结构保护。",
  actionAccelerate: "改变目标倒计时，负数通常表示倒计时减少，正数表示倒计时延后。",
  actionTransform: "把输入目标转化为指定原型集合中的道具。",
  actionSpawn: "生成指定原型道具，可指定位置和生成数量。",
  actionEliminate: "消除输入目标，可限制消除数量；成功、失败和每次分支可继续接后续动作。",
  actionCollect: "每次触发时从目标处收集魔力并暂存在当前辅料中，目标自身不扣产出。",
  actionHarvest: "释放当前辅料已收集的魔力，加入当回合产出。",
  actionRarityWeight: "调整指定品质的抽取权重倍率，适合矿物体系的抽取质量控制。",
  actionExtendCommission: "让当前委托增加一次调和次数，通常用于特定辅料。",
  actionSwapPosition: "让目标与指定位置上的道具交换位置，配合角落、边缘、行列类效果。",
  actionPermanentBoost: "让目标后续产出获得固定值或倍率加成，可用互斥标签避免同类叠加。",
  actionTriggerVisual: "仅用于触发动画和特效，不改变运行数据，不产生描述和强度贡献。",
  intConstant: "输出一个固定整数。",
  intAttribute: "从目标读取整数属性，例如品质、标签或当前产出。",
  intQuantity: "统计输入目标数组中的目标数量。",
  intKindCount: "统计输入目标数组中不同原型的数量。",
  intRandom: "在最小值和最大值之间随机输出整数。",
  intOperation: "对两个整数执行加、减、乘、除、取余、最小或最大运算。",
  floatConstant: "输出一个固定浮点数。",
  floatAttribute: "从目标读取浮点属性。",
  floatRandom: "在最小值和最大值之间随机输出浮点数。",
  floatOperation: "对两个浮点数执行加、减、乘、除、取余、最小或最大运算。",
  roundNumber: "把浮点数按指定规则转换为整数。",
  rarityConstant: "输出一个固定品质配置值。",
  tagConstant: "输出一个固定标签配置值。",
  logicCompareInt: "比较两个整数并输出布尔条件。",
  logicCompareFloat: "比较两个浮点数并输出布尔条件。",
  logicContains: "判断目标数组中是否包含指定目标。",
  logicProbability: "按概率随机输出布尔条件。",
};

const targetAttributeOptions: DescribedOption<AttributeValueNodeData["attribute"]>[] = [
  { value: "rarity", label: "品质", description: "读取目标的品质值。" },
  { value: "tag", label: "标签", description: "读取目标的标签值。" },
  { value: "yield", label: "产出", description: "读取目标当前魔力产出，包含永久增加或减少的产出加成。" },
];

const compareOpOptions: DescribedOption<CompareNodeData["op"]>[] = [
  { value: "eq", label: "等于", description: "左值与右值完全相等时条件成立。" },
  { value: "ne", label: "不等于", description: "左值与右值不相等时条件成立。" },
  { value: "gt", label: "大于", description: "左值严格大于右值时条件成立。" },
  { value: "gte", label: "大于等于", description: "左值大于或等于右值时条件成立。" },
  { value: "lt", label: "小于", description: "左值严格小于右值时条件成立。" },
  { value: "lte", label: "小于等于", description: "左值小于或等于右值时条件成立。" },
];

const integerOperationOptions: DescribedOption<IntegerOperationNodeData["op"]>[] = [
  { value: "add", label: "加", description: "输出左值加右值。" },
  { value: "subtract", label: "减", description: "输出左值减右值。" },
  { value: "multiply", label: "乘", description: "输出左值乘右值。" },
  { value: "divide", label: "除", description: "输出左值除以右值后的向下取整结果；右值为 0 时返回 0。" },
  { value: "mod", label: "取余", description: "输出左值除以右值后的余数；右值为 0 时返回 0。" },
  { value: "min", label: "最小", description: "输出左值和右值中的较小值。" },
  { value: "max", label: "最大", description: "输出左值和右值中的较大值。" },
];

const floatOperationOptions: DescribedOption<FloatOperationNodeData["op"]>[] = [
  { value: "add", label: "加", description: "输出左值加右值。" },
  { value: "subtract", label: "减", description: "输出左值减右值。" },
  { value: "multiply", label: "乘", description: "输出左值乘右值。" },
  { value: "divide", label: "除", description: "输出左值除以右值；右值为 0 时返回 0。" },
  { value: "mod", label: "取余", description: "输出左值除以右值后的余数；右值为 0 时返回 0。" },
  { value: "min", label: "最小", description: "输出左值和右值中的较小值。" },
  { value: "max", label: "最大", description: "输出左值和右值中的较大值。" },
];

const roundModeOptions: DescribedOption<RoundNumberNodeData["mode"]>[] = [
  { value: "round", label: "四舍五入", description: "按最接近的整数取整。" },
  { value: "ceil", label: "向上取整", description: "取大于或等于输入值的最小整数。" },
  { value: "floor", label: "向下取整", description: "取小于或等于输入值的最大整数。" },
];

const boardMaterialScopeOptions: DescribedOption<BoardMaterialScope>[] = [
  { value: "all", label: "全部", description: "输出当前盘面上的全部原料。" },
  { value: "relative", label: "相对位置", description: "按相邻、同行、同列或对角线等相对关系输出原料。" },
  { value: "absolute", label: "绝对位置", description: "按角落、边缘、行列等盘面固定区域输出原料。" },
];

const prototypeLibraryTypeOptions: DescribedOption<PrototypeLibraryNodeData["propType"]>[] = [
  { value: 1, label: "原料", description: "只输出可以进入材料库并上场反应的原料原型。" },
  { value: 2, label: "辅料", description: "只输出不上场、由玩家主动使用或收获的一次性辅料原型。" },
  { value: 3, label: "配方", description: "只输出获得后持续生效的配方光环原型。" },
];

const relativePositionOptions: DescribedOption<BoardMaterialPosition>[] = [
  { value: "adjacent", label: "相邻", description: "筛出与输入目标上下左右相邻的道具。" },
  { value: "sameRow", label: "同行", description: "筛出与输入目标位于同一行的道具。" },
  { value: "sameColumn", label: "同列", description: "筛出与输入目标位于同一列的道具。" },
  { value: "diagonal", label: "对角线", description: "筛出与输入目标处于斜向对角线关系的道具。" },
];

const absolutePositionOptions: DescribedOption<BoardMaterialPosition>[] = [
  { value: "corner", label: "角落", description: "筛出位于盘面四个角落的道具。" },
  { value: "edge", label: "边缘", description: "筛出位于盘面外圈边缘的道具，包含首行、尾行、首列、尾列。" },
  { value: "firstRow", label: "首行", description: "筛出位于盘面第一行的道具。" },
  { value: "lastRow", label: "尾行", description: "筛出位于盘面最后一行的道具。" },
  { value: "firstColumn", label: "首列", description: "筛出位于盘面第一列的道具。" },
  { value: "lastColumn", label: "尾列", description: "筛出位于盘面最后一列的道具。" },
  { value: "middleColumn", label: "中间列", description: "筛出位于盘面中间列的道具。" },
];

export function getPropGraphNodeDocumentTitle(type: PropNodeType) {
  return propGraphNodeDocumentLabels[type] ?? type;
}

export function getPropGraphNodeUsageDoc(type: PropNodeType): PropGraphNodeUsageDoc {
  switch (type) {
    case "eventTurnStart":
      return {
        purpose: "在回合开始时按周期启动流程，适合持续产出、成长、倒计时和配方光环。",
        scenes: ["每轮固定效果。", "每 N 轮触发。", "延迟一次性效果。"],
        examples: [
          {
            title: "每 3 轮成长",
            graph: "回合开始(interval=2) -> 成长(targets=自身, amount=1)",
            result: "每 3 轮当前道具魔力永久+1。",
            note: "interval 存储为间隔轮数，实际周期是 interval + 1。",
          },
          {
            title: "3 轮后转化",
            graph: "回合开始(interval=2, loop=1) -> 转化(targets=自身, prototypes=原型)",
            result: "3 轮后转化为指定道具。",
            note: "loop=1 表示最多触发一次，适合延迟一次性效果。",
          },
        ],
        pitfalls: [
          "不要把 interval=3 理解为每 3 轮，实际会变成每 4 轮。",
          "loop 为空或 0 表示不限制触发次数，不是不触发。",
          "周期事件需要按实例记录触发次数，不能全道具共用一个计数。",
        ],
      };
    case "eventAcquire":
      return {
        purpose: "在道具处于持有状态时持续生效，适合材料库、配方区或辅料状态上的长期规则。",
        scenes: ["品质权重调整。", "配方光环。", "持有期间持续生效的永久加成。"],
        examples: [
          {
            title: "提高品质概率",
            graph: "持有触发 -> 品质权重(rarity=罕见, multiplier=2)",
            result: "持有时罕见物品抽取概率x2。",
          },
          {
            title: "持有光环",
            graph: "持有触发 -> 额外产出(targets=标签筛选(矿物), amount=3)",
            result: "持有期间矿物魔力+3。",
          },
        ],
        pitfalls: [
          "持有触发不是获得瞬间触发一次，而是持有期间持续检查。",
          "道具被移除后必须移除对应效果，尤其是品质权重和光环。",
          "不要要求持有触发的道具必须在盘面上。",
        ],
      };
    case "eventUse":
      return {
        purpose: "玩家主动使用辅料或可使用道具时启动效果，是一次性辅料和收获类辅料的入口。",
        scenes: ["主动生成、转化或消除。", "释放收集到的魔力。", "使用后提供持续生效的加成。"],
        examples: [
          {
            title: "使用后生成",
            graph: "主动使用 -> 生成(targets=原型(植物种子), amount=1)",
            result: "使用后生成植物种子。",
          },
          {
            title: "使用后永久加成",
            graph: "主动使用 -> 永久加成(targets=原料, multiplier=1.2)",
            result: "使用后原料魔力x1.2，持续生效。",
          },
        ],
        pitfalls: [
          "主动使用触发本身只执行一次，但永久加成、品质权重等动作可以产生持续效果。",
          "已消耗或不满足使用条件的辅料不应触发事件。",
          "主动使用后的效果应直接承接触发语义，不额外引入停顿。",
        ],
      };
    case "eventEliminated":
      return {
        purpose: "监听指定目标在当前轮内是否被真正消除，并在消除后启动后续效果。",
        scenes: ["自毁后生成。", "目标死亡后连锁成长。", "灰烬、凤凰、蘑菇等死亡触发效果。"],
        examples: [
          {
            title: "被消除后生成",
            graph: "消除触发(target=自身) -> 生成(targets=原型(灰烬), amount=1)",
            result: "被消除后生成灰烬。",
          },
          {
            title: "目标被消除后成长",
            graph: "消除触发(target=相邻昆虫) -> 成长(targets=蘑菇, amount=5)",
            result: "相邻昆虫被消除后蘑菇魔力永久+5。",
          },
        ],
        pitfalls: [
          "每轮开始注册监听，每轮结束注销监听，不能永久挂监听。",
          "受保护目标被消除动作命中但没有真正移除时，不触发被消除事件。",
          "监听自身时，需要在实例销毁前保留上下文给后续流程使用。",
        ],
      };
    case "targetSelf":
      return createTargetUsageDoc("自身", "返回当前正在执行效果的道具实例，常用于让动作作用于自己。", "自身 -> 成长(targets=自身, amount=1)", "当前道具魔力永久+1。");
    case "targetBoard":
      return createTargetUsageDoc("盘面原料", "从当前盘面取出一组原料目标，供筛选、条件判断或动作使用。", "盘面原料(scope=all) -> 标签筛选(植物) -> 成长(amount=1)", "植物魔力永久+1。", [
        "scope=all 时位置参数应为空；运行判断不会读取 position。",
        "scope=relative 需要上下文目标作为锚点，没有锚点时结果为空。",
        "相对位置和绝对位置不能混用。",
      ]);
    case "prototypeLibrary":
      return createTargetUsageDoc("原型库", "取出某一类型的道具原型集合，通常作为随机生成或随机转化的候选池。", "原型库(propType=原料) -> 标签筛选(植物) -> 生成", "生成随机植物。", [
        "原型库输出的是原型，不是盘面实例。",
        "生成和转化原料时，通常 propType 应选择原料。",
      ]);
    case "prototypeSelect":
      return createTargetUsageDoc("原型", "选择一个固定道具原型，常用于指定生成、指定转化或原型筛选。", "原型(灰烬) -> 生成(amount=1)", "生成灰烬。", [
        "prototypeId 必须是道具原型 _id，不是盘面实例 id。",
        "原型为空时，下游生成或转化应失败。",
      ]);
    case "targetFilter":
      return createFilterUsageDoc("条件筛选", "用布尔条件逐个过滤目标数组，保留条件成立的目标。", "盘面原料 -> 条件筛选(condition=概率50%) -> 额外产出", "约一半原料获得额外产出。");
    case "filterRarity":
      return createFilterUsageDoc("品质筛选", "按品质保留目标，适合普通、少见、稀有、罕见相关道具。", "盘面原料 -> 品质筛选(普通、少见) -> 消除", "消除普通或少见原料。");
    case "filterTag":
      return createFilterUsageDoc("标签筛选", "按体系标签保留目标，是植物、昆虫、矿物、蘑菇、羽毛、液体等体系互动的常用节点。", "盘面原料 -> 标签筛选(植物) -> 成长", "植物魔力永久+1。");
    case "filterPrototype":
      return createFilterUsageDoc("原型筛选", "按指定道具原型保留目标，用于具体道具关系。", "盘面原料 -> 原型筛选(银月草) -> 包含", "判断场上是否有银月草。", [
        "比较实例的原型 id，不比较实例 id。",
        "原型输入可多连，多连表示多个具体道具候选。",
      ]);
    case "filterRandom":
      return createFilterUsageDoc("随机筛选", "从目标数组中随机抽取指定数量，常用于“随机 N 个目标”的效果。", "植物 -> 随机筛选(count=2) -> 成长(amount=1)", "随机 2 个植物魔力永久+1。", [
        "count 为 0 时返回空目标数组。",
        "随机节点应使用统一随机源，保证运行结果可复现。",
      ]);
    case "actionGrow":
      return createActionUsageDoc("成长", "永久改变目标后续基础魔力，是长期培养和衰减体系的核心动作。", "回合开始 -> 成长(targets=植物, amount=1)", "植物魔力永久+1。");
    case "actionExtraYield":
      return createActionUsageDoc("额外产出", "立即改变本次魔力产出，可用固定值或倍率。", "回合开始 -> 额外产出(targets=相邻液体, amount=2)", "相邻液体魔力+2。");
    case "actionPermanentBoost":
      return createActionUsageDoc("永久加成", "给一批目标注册持续生效的固定值或倍率加成。", "主动使用 -> 永久加成(targets=少见原料, multiplier=1.2, mutexTag=饰品)", "使用后少见原料魔力x1.2，持续生效，饰品效果不叠加。");
    case "actionPurify":
      return createActionUsageDoc("净化", "移除由负面动作标记产生的负面效果。", "回合开始 -> 净化(targets=蘑菇)", "净化蘑菇负面效果。", ["只有 negative=true 的效果才是净化对象。"]);
    case "actionProtect":
      return createActionUsageDoc("保护", "给目标提供消除拦截能力，使目标被消除动作命中时保留下来。", "回合开始 -> 保护(targets=相邻蘑菇)", "保护相邻蘑菇不被消除。", [
        "消除受保护目标时，消除动作视为成功，但目标不会被移除。",
        "被保护拦截不触发被消除事件。",
      ]);
    case "actionAccelerate":
      return createActionUsageDoc("加速", "修改目标倒计时，负数通常代表提前触发，正数代表延后。", "回合开始 -> 加速(targets=相邻液体, amount=-1)", "相邻液体倒计时-1。");
    case "actionTransform":
      return createActionUsageDoc("转化", "把目标实例替换为另一种原型，常用于进化、腐化、炼成和品质升级。", "回合开始 -> 转化(targets=相邻工蜂, prototypes=玫瑰蜂)", "转化相邻工蜂为玫瑰蜂。");
    case "actionSpawn":
      return createActionUsageDoc("生成", "创建新道具实例，通常会改变材料库或盘面。", "主动使用 -> 生成(targets=植物种子, amount=3)", "使用后生成 3 个植物种子。", [
        "生成低品质原料在后期可能是负收益，因为会占用材料库和盘面位置。",
      ]);
    case "actionEliminate":
      return createActionUsageDoc("消除", "移除目标实例，常用于吞噬、自毁、清理低价值原料或触发被消除连锁。", "回合开始 -> 消除(targets=相邻植物, amount=1) -> 完成: 成长(amount=5)", "消除 1 个相邻植物，成功则魔力永久+5。");
    case "actionCollect":
      return createActionUsageDoc("收集", "把目标提供的魔力暂存到当前辅料，目标自身不会损失魔力。", "每轮 -> 收集(targets=羽毛, amount=2)", "从每个羽毛收集 2 点魔力。");
    case "actionHarvest":
      return createActionUsageDoc("收获", "释放当前辅料已收集的魔力，加入当回合产出。", "主动使用 -> 收获", "使用后获得已收集的魔力。");
    case "actionRarityWeight":
      return createActionUsageDoc("品质权重", "修改某一品质的抽取权重倍率，影响后续候选项质量。", "持有触发 -> 品质权重(rarity=罕见, multiplier=2)", "持有时罕见物品抽取概率x2。");
    case "actionExtendCommission":
      return createActionUsageDoc("延长调和", "增加当前委托调和次数。", "主动使用 -> 延长调和", "调和次数+1，每份委托限用 1 次。");
    case "actionSwapPosition":
      return createActionUsageDoc("调换位置", "让目标与指定位置上的道具交换位置，配合位置类效果。", "回合开始 -> 调换位置(targets=羽毛, positions=角落)", "交换羽毛到角落。");
    case "intConstant":
    case "floatConstant":
    case "rarityConstant":
    case "tagConstant":
      return createValueUsageDoc(getPropGraphNodeDocumentTitle(type), propGraphNodeDocumentSummaries[type], `${getPropGraphNodeDocumentTitle(type)} -> 下游输入`, "提供固定输入值。");
    case "intAttribute":
    case "floatAttribute":
      return createValueUsageDoc(getPropGraphNodeDocumentTitle(type), "从目标读取属性值，供比较、计算或动作使用。", `${getPropGraphNodeDocumentTitle(type)}(产出) -> 比较`, "按目标产出判断条件。");
    case "intQuantity":
      return createValueUsageDoc("数量", "统计目标数组长度，用于表达“每有 N 个目标”或按数量计算收益。", "植物 -> 数量 -> 整数运算", "按植物数量计算收益。");
    case "intKindCount":
      return createValueUsageDoc("种类", "统计目标数组中不同原型数量，用于种类门槛。", "蘑菇 -> 种类 -> 整数比较(>=3)", "场上有至少 3 种蘑菇时条件成立。");
    case "intRandom":
    case "floatRandom":
      return createValueUsageDoc(getPropGraphNodeDocumentTitle(type), "在指定范围内随机取值，用于随机收益或随机生成数量。", "随机(0, 10) -> 额外产出(amount)", "魔力+0~10。");
    case "intOperation":
    case "floatOperation":
      return createValueUsageDoc(getPropGraphNodeDocumentTitle(type), "组合两个数值，表达加减乘除、上下限或比例公式。", "数量 ÷ 3 -> 取整 -> 成长(amount)", "每有 3 个目标产生 1 点成长。");
    case "roundNumber":
      return createValueUsageDoc("取整", "把浮点结果转换为整数，常用于把比例收益变成目标数量或整数魔力。", "浮点运算 -> 取整(mode=floor) -> 生成(amount)", "按向下取整后的结果生成目标。");
    case "logicCompareInt":
    case "logicCompareFloat":
      return createConditionUsageDoc(getPropGraphNodeDocumentTitle(type), "比较两个数值并输出条件。", "数量 >= 3 -> 动作(condition)", "数量达到门槛时动作触发。");
    case "logicContains":
      return createConditionUsageDoc("包含", "判断一个目标集合中是否存在指定目标。", "角落原料 + 自身 -> 包含", "位于角落时条件成立。");
    case "logicProbability":
      return createConditionUsageDoc("概率", "按指定概率随机返回条件结果。", "概率(0.3) -> 动作(condition)", "30% 概率触发动作。");
    default:
      return createValueUsageDoc(getPropGraphNodeDocumentTitle(type), propGraphNodeDocumentSummaries[type], `${getPropGraphNodeDocumentTitle(type)} -> 下游节点`, "按端口语义提供运行时数据。");
  }
}

function createTargetUsageDoc(title: string, purpose: string, graph: string, result: string, pitfalls: string[] = []): PropGraphNodeUsageDoc {
  return {
    purpose,
    scenes: ["为动作准备目标。", "为筛选或条件节点准备输入。", "作为生成、转化或消除的对象来源。"],
    examples: [{ title: `${title}示例`, graph, result }],
    pitfalls: [
      "目标端口统一按数组语义传递。",
      "目标为空时，下游动作通常走失败分支。",
      ...pitfalls,
    ],
  };
}

function createFilterUsageDoc(title: string, purpose: string, graph: string, result: string, pitfalls: string[] = []): PropGraphNodeUsageDoc {
  return {
    purpose,
    scenes: ["缩小目标范围。", "表达标签、品质、原型或条件限制。", "为随机筛选或动作节点准备更精确的对象。"],
    examples: [{ title: `${title}示例`, graph, result }],
    pitfalls: [
      "筛选节点不会推动流程，只返回筛选后的目标数组。",
      "输入目标为空时，输出也为空。",
      ...pitfalls,
    ],
  };
}

function createActionUsageDoc(title: string, purpose: string, graph: string, result: string, pitfalls: string[] = []): PropGraphNodeUsageDoc {
  return {
    purpose,
    scenes: ["接收流程后改变魔力、状态、位置或材料库。", "通过条件端口控制是否触发。", "通过完成、失败、每次分支连接后续动作。"],
    examples: [{ title: `${title}示例`, graph, result }],
    pitfalls: [
      "动作没有流程输入时不会主动执行。",
      "条件端口多连时需要全部成立。",
      "目标为空时通常走失败分支。",
      ...pitfalls,
    ],
  };
}

function createValueUsageDoc(title: string, purpose: string, graph: string, result: string, pitfalls: string[] = []): PropGraphNodeUsageDoc {
  return {
    purpose,
    scenes: ["为动作端口提供数量、倍率或条件输入。", "参与比较和计算。", "把规则常量转成可连接的端口输出。"],
    examples: [{ title: `${title}示例`, graph, result }],
    pitfalls: [
      "取值节点只提供数据，不推动流程。",
      "输入缺失时应按节点规则返回默认值或记录规则错误。",
      ...pitfalls,
    ],
  };
}

function createConditionUsageDoc(title: string, purpose: string, graph: string, result: string, pitfalls: string[] = []): PropGraphNodeUsageDoc {
  return {
    purpose,
    scenes: ["控制动作是否触发。", "表达概率、比较和包含关系。", "和目标筛选组合形成条件目标池。"],
    examples: [{ title: `${title}示例`, graph, result }],
    pitfalls: [
      "条件节点只返回布尔值，不会推动流程。",
      "多个条件连到同一动作时按全部成立处理。",
      ...pitfalls,
    ],
  };
}

export function getPropGraphNodeRuntimeDetail(type: PropNodeType) {
  if (type.startsWith("event")) {
    return "事件节点在满足自身触发条件时调用输出流程函数。事件本身不产生目标或数值，只负责启动后续动作链。";
  }
  if (type.startsWith("target") || type.startsWith("prototype")) {
    return "目标节点输出目标函数。下游节点求值时调用该函数，返回道具实例数组或道具原型数组。";
  }
  if (type.startsWith("filter")) {
    return "筛选节点接收上游目标函数，运行时求值后返回过滤后的目标数组。";
  }
  if (type.startsWith("action")) {
    return "动作节点接收流程函数调用后执行。执行前会求值目标、数量、倍率和条件输入，并根据结果调用完成、失败或每次分支。";
  }
  if (type.startsWith("logic")) {
    return "条件节点输出布尔函数。动作或筛选节点需要判断条件时调用该函数。";
  }
  return "取值和计算节点通过输出端口提供数值函数或配置函数。下游输入端口求值时调用函数，返回整数、浮点数、品质或标签等端口数据类型。";
}

const numericParameterDescriptions = {
  integer: "允许有限整数，支持负数、0 和正数；禁止小数、NaN、Infinity、空值和非数字文本。",
  nonNegativeInteger: "允许 0 或正整数；禁止负数、小数、NaN、Infinity、空值和非数字文本。",
  optionalNonNegativeInteger: "允许空值、0 或正整数；禁止负数、小数、NaN、Infinity 和非数字文本。",
  float: "允许有限浮点数，支持负数、0 和正数；禁止 NaN、Infinity、空值和非数字文本。",
  probability: "允许 0 到 1 之间的有限浮点数，0 表示永不触发，1 表示必定触发；禁止负数、大于 1、NaN、Infinity、空值和非数字文本。",
};

export function getPropGraphNodeParameterDocs(type: PropNodeType): PropGraphNodeParameterDoc[] {
  const actionParams: PropGraphNodeParameterDoc[] = propGraphActionNodeTypes.includes(type)
    ? [
      {
        name: "负面",
        field: "negative",
        required: "否",
        description: "布尔值。true 表示该动作产生负面效果，可被净化动作移除；false 表示普通效果。未设置时按 false 处理。",
      },
      {
        name: "自身特效",
        field: "selfEffects",
        required: "否",
        description: "允许为空数组或特效资源 ID 数组。触发时在当前道具自身播放。",
      },
      {
        name: "目标特效",
        field: "targetEffects",
        required: "否",
        description: "允许为空数组或特效资源 ID 数组。动作作用到目标时播放。",
      },
      {
        name: "自身动画",
        field: "selfAnimations",
        required: "否",
        description: "允许为空数组或动画资源 ID 数组。动作触发时在当前道具自身播放。",
      },
      {
        name: "目标动画",
        field: "targetAnimations",
        required: "否",
        description: "允许为空数组或动画资源 ID 数组。动作作用到目标时播放。",
      },
    ]
    : [];

  switch (type) {
    case "eventTurnStart":
      return [
        {
          name: "间隔",
          field: "interval",
          required: "是",
          description: `${numericParameterDescriptions.nonNegativeInteger} 0 表示每轮触发；1 表示每 2 轮触发；2 表示每 3 轮触发。`,
        },
        {
          name: "循环",
          field: "loop",
          required: "否",
          description: `${numericParameterDescriptions.optionalNonNegativeInteger} 空值或 0 都表示不限制触发次数；正整数表示最多触发对应次数。`,
        },
      ];
    case "targetBoard":
      return [
        {
          name: "范围",
          field: "scope",
          required: "是",
          description: renderEnumParameterDescription({
            options: boardMaterialScopeOptions,
            forbidden: "禁止使用其它字符串或空值。",
          }),
        },
        {
          name: "位置",
          field: "position",
          required: "条件必填",
          description: renderBoardPositionParameterDescription(),
        },
        {
          name: "包含空位置",
          field: "includeEmpty",
          required: "否",
          description: "布尔值，默认 false。false 表示只输出真实盘面原料；true 表示同时输出数据库“空位置”原型创建的盘面占位。用于统计空位置、筛选空位置、生成到空位置或交换到空位置等效果；普通目标效果建议保持默认 false。",
        },
      ];
    case "prototypeLibrary":
      return [
        {
          name: "类型",
          field: "propType",
          required: "否",
          description: renderEnumParameterDescription({
            intro: "默认值为 1（原料）。",
            options: prototypeLibraryTypeOptions,
            forbidden: "禁止使用 1、2、3 之外的数字或任意字符串。",
          }),
        },
      ];
    case "prototypeSelect":
      return [
        {
          name: "道具原型",
          field: "prototypeId",
          required: "是",
          description: "允许现有 propgraphs._id；禁止填写实例 ID、空字符串、不存在的道具 ID 或任意文本。运行时比较实例的原型 ID。",
        },
      ];
    case "intConstant":
      return [{ name: "值", field: "value", required: "是", description: numericParameterDescriptions.integer }];
    case "floatConstant":
      return [{ name: "值", field: "value", required: "是", description: numericParameterDescriptions.float }];
    case "intAttribute":
    case "floatAttribute":
      return [{
        name: "属性",
        field: "attribute",
        required: "是",
        description: renderEnumParameterDescription({ options: targetAttributeOptions, forbidden: "禁止使用其它字符串或空值。" }),
      }];
    case "intOperation":
      return [{
        name: "运算符",
        field: "op",
        required: "是",
        description: renderEnumParameterDescription({ options: integerOperationOptions, extra: "除法和取余的右值为 0 时按运行器规则返回 0。", forbidden: "禁止使用其它字符串或空值。" }),
      }];
    case "floatOperation":
      return [{
        name: "运算符",
        field: "op",
        required: "是",
        description: renderEnumParameterDescription({ options: floatOperationOptions, extra: "除法和取余的右值为 0 时按运行器规则返回 0。", forbidden: "禁止使用其它字符串或空值。" }),
      }];
    case "roundNumber":
      return [{
        name: "取整方式",
        field: "mode",
        required: "是",
        description: renderEnumParameterDescription({ options: roundModeOptions, forbidden: "禁止使用其它字符串或空值。" }),
      }];
    case "rarityConstant":
      return [{ name: "配置值", field: "value", required: "是", description: "枚举值来自品质配置表 proprarities。允许现有品质值；禁止不存在的品质值、空值或任意文本。" }];
    case "tagConstant":
      return [{ name: "配置值", field: "value", required: "是", description: "枚举值来自标签配置表 proptags。允许现有标签值；禁止不存在的标签值、空值或任意文本。" }];
    case "logicCompareInt":
    case "logicCompareFloat":
      return [{
        name: "比较符",
        field: "op",
        required: "是",
        description: renderEnumParameterDescription({ options: compareOpOptions, forbidden: "禁止使用其它字符串或空值。" }),
      }];
    case "logicProbability":
      return [{ name: "概率", field: "probability", required: "是", description: numericParameterDescriptions.probability }];
    case "actionPermanentBoost":
      return [
        ...actionParams,
        {
          name: "互斥标签",
          field: "mutexTag",
          required: "否",
          description: "允许任意非空字符串，建议使用稳定业务标签，例如“中和”“饰品”。空值按 default 处理；禁止依赖随机文本表达不同效果。同一目标上相同互斥标签的永久加成不叠加。",
        },
      ];
    default:
      return actionParams;
  }
}

function renderEnumParameterDescription<T extends OptionValue>({
  intro,
  options,
  forbidden,
  extra,
}: {
  intro?: ReactNode;
  options: DescribedOption<T>[];
  forbidden?: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div>
      {intro && <p>{intro}</p>}
      <p>允许值：</p>
      <ul>
        {options.map((option) => (
          <li key={String(option.value)}>
            <code>{String(option.value)}</code> = {option.label}
            {option.description ? `：${option.description}` : ""}
          </li>
        ))}
      </ul>
      {extra && <p>{extra}</p>}
      {forbidden && <p>{forbidden}</p>}
    </div>
  );
}

function renderBoardPositionParameterDescription() {
  return (
    <div>
      <p><code>scope=all</code> 时允许为空；运行判断不使用该字段。</p>
      <p><code>scope=relative</code> 时必填，允许以下相对位置：</p>
      <ul>
        {relativePositionOptions.map((option) => (
          <li key={option.value}><code>{option.value}</code> = {option.label}：{option.description}</li>
        ))}
      </ul>
      <p><code>scope=absolute</code> 时必填，允许以下绝对位置：</p>
      <ul>
        {absolutePositionOptions.map((option) => (
          <li key={option.value}><code>{option.value}</code> = {option.label}：{option.description}</li>
        ))}
      </ul>
      <p>禁止混用相对位置和绝对位置；scope 为 relative 或 absolute 时禁止空数组。</p>
    </div>
  );
}
